import api from '../config/api';
import { removeEmpty } from '../helpers/objects';

/**
 * Registra um novo usuário no sistema.
 *
 * @param {Object} user - Os dados do usuário para registro.
 * @returns {Promise<Object>} - Retorna os dados do usuário registrado.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const registerUser = async user => {
  const { data } = await api.post('/api/auth/local/register', user);
  return data;
};

/**
 * Obtém os dados do usuário logado.
 *
 * @returns {Promise<Object>} - Retorna os dados do usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const userData = async () => {
  try {
    const url = '/api/users/me';
    const { data } = await api.get(url);

    // Garantindo que os campos 'name' e 'phone' não sejam nulos
    if (data.name == null) data.name = '';
    if (data.phone == null) data.phone = '';

    return data;
  } catch (error) {
    console.error('❌ [services/users.js] ~ [userData] ~ Erro ao obter dados do usuário:', error.message);
    throw error;
  }
};

/**
 * Obtém os dados dos usuários SubUsers que pertencem aos mesmos grupos do OrgAdmin logado.
 *
 * @returns {Promise<Array>} - Retorna uma lista de usuários SubUsers dos mesmos grupos do OrgAdmin logado.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const getSubUsers = async () => {
  try {
    // Primeiro, obtém os dados do usuário logado (OrgAdmin) com seus grupos
    const currentUserData = await userDataGroup();

    if (!currentUserData.groups || currentUserData.groups.length === 0) {
      console.warn('⚠️ [getSubUsers] ~ OrgAdmin logado não possui grupos vinculados');
      return [];
    }

    // Obtém os IDs dos grupos do OrgAdmin logado
    const groupIds = currentUserData.groups.map(group => group.id);

    // Busca todos os SubUsers
    const url = `/api/users?populate=role&populate=groups&populate=sub_groups&populate=type&filters[type][$eq]=SubUser`;
    const { data } = await api.get(url);

    // Filtra SubUsers que pertencem aos mesmos grupos do OrgAdmin logado
    const filteredSubUsers = data.filter(user => {
      if (!user.groups || user.groups.length === 0) return false;

      // Verifica se o SubUser tem pelo menos um grupo em comum com o OrgAdmin
      return user.groups.some(userGroup => groupIds.includes(userGroup.id));
    });

    // Garantindo que os campos 'name' e 'phone' não sejam nulos
    return filteredSubUsers.map(user => ({
      ...user,
      name: user.name || '',
      phone: user.phone || '',
    }));
  } catch (error) {
    console.error('❌ [services/users.js] ~ [getSubUsers] ~ Erro ao buscar sub-usuários:', error.message);
    throw new Error('Erro ao buscar Usuários.');
  }
};

/**
 * Obtém regras do usuário logado.
 *
 * @returns {Promise<Object>} - Retorna as regras do usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const userDataRoles = async () => {
  try {
    const url = '/api/users/me?populate=role';
    const { data } = await api.get(url);

    // Garantindo que os campos 'name' e 'phone' não sejam nulos
    if (data.name == null) data.name = '';
    if (data.phone == null) data.phone = '';

    return data;
  } catch (error) {
    console.error('❌ [services/users.js] ~ [userDataRoles] ~ Erro ao obter regras do usuário:', error.message);
    throw error;
  }
};

/**
 * Atualiza os dados do usuário com base no ID e payload fornecidos.
 *
 * @param {Object} payload - Os dados a serem atualizados.
 * @param {string} id - O ID do usuário a ser atualizado.
 * @returns {Promise<Object>} - Retorna os dados atualizados do usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const updateData = async (payload, id) => {
  try {
    // Enviando requisição para atualização de usuário

    const url = `/api/users/${id}`;
    const { data } = await api.put(url, removeEmpty(payload));

    // Resposta recebida com sucesso
    return data;
  } catch (error) {
    console.error('❌ [services/users.js] ~ [updateData] ~ ERRO HTTP DETECTADO:');
    console.error('- Error object:', error);
    console.error('- Error message:', error.message);
    console.error('- Response status:', error.response?.status);
    console.error('- Response data:', error.response?.data);
    console.error('- Response headers:', error.response?.headers);
    console.error('- Request config:', error.config);
    throw error;
  }
};

/**
 * Obtém os dados do usuário logado com o grupo populado.
 *
 * @returns {Promise<Object>} - Retorna os dados do usuário com o grupo populado.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const userDataGroup = async () => {
  try {
    const url = `/api/users/me?populate=groups`;
    const { data } = await api.get(url);

    return data;
  } catch (error) {
    console.error(
      '❌ [services/users.js] ~ [userDataGroup] ~ Erro ao obter dados do usuário com grupo:',
      error.message
    );
    throw error;
  }
};

/**
 * Obtém os dados do usuário logado com o subgrupo populado.
 *
 * @returns {Promise<Object>} - Retorna os dados do usuário com o subgrupo populado.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const userDataSubGroup = async () => {
  try {
    const url = `/api/users/me?populate=sub_groups`;
    const { data } = await api.get(url);

    if (data.sub_group == null) data.sub_group = '';

    return data;
  } catch (error) {
    console.error(
      '❌ [services/users.js] ~ [userDataSubGroup] ~ Erro ao obter dados do usuário com subgrupo:',
      error.message
    );
    throw error;
  }
};

/**
 * Remove um usuário do sistema com base no ID.
 *
 * @param {string} id - O ID do usuário a ser removido.
 * @returns {Promise<Object>} - Retorna a resposta da remoção do usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const deleteUser = async id => {
  try {
    const url = `/api/users/${id}`;
    const { data } = await api.delete(url);
    return data;
  } catch (error) {
    console.error('❌ services/users.js ~ deleteUser ~ Erro:', error.response ? error.response.data : error.message);
    throw error;
  }
};

/**
 * Verifica se o CPF ou CNPJ já existe no banco de dados.
 *
 * @param {string} cpfCnpj - O CPF ou CNPJ a ser verificado.
 * @returns {Promise<boolean>} - Retorna true se o CPF ou CNPJ já existir, caso contrário false.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const checkCpfCnpjExists = async cpfCnpj => {
  const raw = cpfCnpj.replace(/\D/g, '');
  const { data } = await api.post('/api/groups/check-cpf-cnpj', { cpfCnpj: raw });
  return data.exists;
};

/**
 * Obtém a assinatura vinculada ao usuário pelo ID.
 *
 * @param {string} userId - O ID do usuário.
 * @returns {Promise<Object>} - Retorna os dados da assinatura vinculada ao usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const getUserSubscriptions = async () => {
  const url = `/api/users/me?populate=subscription`;
  try {
    const { data } = await api.get(url);
    return {
      id: data.id,
      subscription: data.subscription,
      subscriptionScheduling: data.subscriptionScheduling,
      createdAt: data.createdAt,
    }; // Retorna o ID do usuário, a assinatura, agendamento e a data de criação
  } catch (error) {
    console.error('❌ [users.js] ~ getUserSubscriptions ~ Erro ao buscar assinatura do usuário:', error.message);
    throw new Error('Erro ao buscar assinatura do usuário.');
  }
};

/**
 * Atualiza a assinatura vinculada ao usuário no Strapi.
 *
 * @param {string} userId - ID do usuário no Strapi.
 * @param {string} subscriptionId - ID da assinatura no Strapi.
 * @returns {Promise<Object>} - Dados atualizados do usuário.
 */
export const updateUserSubscription = async (userId, subscriptionId) => {
  // Agora apenas atualiza a subscription, já que não temos mais subscription_due_date
  const payload = { subscription: subscriptionId };
  return await updateData(payload, userId);
};

/**
 * Agenda uma mudança de plano para uma data específica.
 *
 * @param {string} userId - ID do usuário no Strapi.
 * @param {string} schedulingDate - Data de agendamento no formato YYYY-MM-DD.
 * @returns {Promise<Object>} - Dados atualizados do usuário.
 */
export const scheduleSubscriptionChange = async (userId, schedulingDate) => {
  const payload = { subscriptionScheduling: schedulingDate };
  return await updateData(payload, userId);
};

/**
 * Cancela agendamento de mudança de plano.
 *
 * @param {string} userId - ID do usuário no Strapi.
 * @returns {Promise<Object>} - Dados atualizados do usuário.
 */
export const cancelSubscriptionScheduling = async userId => {
  // Abordagem direta: usar uma chamada PUT específica para limpar o campo
  try {
    // Fazer chamada direta para o endpoint do Strapi
    const result = await api.put(`/api/users/${userId}`, {
      subscriptionScheduling: null,
    });

    return result.data;
  } catch (error) {
    console.error('❌ [services/users.js] ~ Erro ao cancelar agendamento:', error);
    throw error;
  }
};

/**
 * Obtém apenas o ID do usuário logado para uso em filtros.
 *
 * @returns {Promise<number>} - Retorna o ID do usuário.
 * @throws {Error} - Lança um erro caso ocorra um erro na requisição.
 */
export const getCurrentUserId = async () => {
  try {
    const data = await userData();
    return data.id;
  } catch (error) {
    console.error('❌ services/users.js ~ getCurrentUserId ~ Erro:', error.message);
    throw error;
  }
};

/**
 * Determina quais menus devem ser exibidos baseado no tipo de usuário e plano de assinatura.
 *
 * @param {string} userType - Tipo do usuário ('OrgAdmin' ou 'SubUser')
 * @param {string} subscriptionPlan - Plano de assinatura ('demo', 'basic', 'professional', 'teste', 'blocked')
 * @param {boolean} deviceManager - Se é device manager (apenas para SubUser)
 * @returns {Object} - Objeto com os menus permitidos
 */
export const getAllowedMenus = (userType, subscriptionPlan, deviceManager = false) => {
  const menus = {
    perfil: false,
    organization: false,
    subuser: false,
    apidoc: false,
    alerts: false,
    payments: false,
    devices: false,
  };

  if (userType === 'OrgAdmin') {
    switch (subscriptionPlan.toLowerCase()) {
      case 'demo':
        menus.perfil = true;
        menus.organization = true;
        menus.payments = true;
        menus.devices = true;
        break;
      case 'basic':
        menus.perfil = true;
        menus.organization = true;
        menus.subuser = true;
        menus.payments = true;
        menus.devices = true;
        break;
      case 'professional':
      case 'teste':
        menus.perfil = true;
        menus.organization = true;
        menus.subuser = true;
        menus.apidoc = true;
        menus.alerts = true;
        menus.payments = true;
        menus.devices = true;
        break;
      case 'blocked':
        menus.perfil = true;
        menus.payments = true;
        break;
      default:
        // Para planos não reconhecidos
        menus.perfil = true;
        menus.payments = true;
        break;
    }
  } else if (userType === 'SubUser') {
    menus.perfil = true;
    menus.devices = true;
    if (deviceManager) {
      menus.organization = true;
    }
  }

  return menus;
};

/**
 * Envia e-mail de confirmação para um usuário específico
 * @param {string} email - E-mail do usuário
 * @returns {Promise} Resposta da API
 */
export const sendConfirmationEmail = async email => {
  try {
    const { data } = await api.post('/api/auth/send-email-confirmation', {
      email,
    });
    return data;
  } catch (error) {
    console.error(
      '❌ services/users.js ~ sendConfirmationEmail ~ Erro:',
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

/**
 * Verifica se um e-mail já existe no sistema
 * @param {string} email - E-mail a ser verificado
 * @returns {Promise<boolean>} True se o e-mail já existe, false caso contrário
 */
export const checkEmailExists = async email => {
  try {
    const { data } = await api.post('/api/groups/check-email', { email });
    return data.exists;
  } catch {
    return false;
  }
};

export const checkGroupExists = async name => {
  try {
    const { data } = await api.post('/api/groups/check-group', { name });
    return data.exists;
  } catch {
    return false;
  }
};

export const checkShortNameExists = async shortName => {
  try {
    const { data } = await api.post('/api/groups/check-short-name', { shortName });
    return data.exists;
  } catch {
    return false;
  }
};

export default {
  registerUser,
  getSubUsers,
  userData,
  updateData,
  userDataGroup,
  userDataSubGroup,
  deleteUser,
  checkCpfCnpjExists,
  checkGroupExists,
  checkShortNameExists,
  getUserSubscriptions,
  updateUserSubscription,
  getCurrentUserId,
  getAllowedMenus,
  sendConfirmationEmail,
  checkEmailExists,
};
